// Лимиты по категориям с прогрессом, at-risk алертами и CRUD.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Category, LimitWithProgress } from '../api/types';
import { CategoryMark } from '../components/CategoryMark';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PencilIcon, TrashIcon } from '../components/icons';
import { ScreenHeader } from '../components/ScreenHeader';
import { formatWithCurrency } from '../lib/money';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { useBackButton, useMainButton } from '../lib/use-main-button';

type Props = {
  onBack: () => void;
};

const CURRENCY = 'UZS';

export function Limits({ onBack }: Props): JSX.Element {
  useBackButton(onBack);
  const [editing, setEditing] = useState<LimitWithProgress | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<LimitWithProgress | null>(null);

  const limitsQ = useQuery({ queryKey: ['limits'], queryFn: () => api.limits() });
  const queryClient = useQueryClient();
  const toast = useToast();

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteLimit(id),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['limits'] });
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось удалить лимит')),
  });

  const data = limitsQ.data;

  return (
    <div className="screen">
      <ScreenHeader
        title="Лимиты"
        onBack={onBack}
        right={
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              haptic('light');
              setCreating(true);
            }}
          >
            + Новый
          </button>
        }
      />

      {limitsQ.isLoading ? (
        <div className="skeleton" style={{ height: 220 }} />
      ) : !data || data.limits.length === 0 ? (
        <div className="empty-mini">
          Лимитов пока нет. Добавьте первый — например, на кафе или транспорт.
        </div>
      ) : (
        <>
          <div className="screen-note">
            День {data.days_passed} из {data.days_in_month} в этом месяце
          </div>
          <div className="limits-list">
            {data.limits.map((l) => (
              <LimitRow
                key={l.id}
                limit={l}
                onEdit={() => setEditing(l)}
                onDelete={() => setConfirmDelete(l)}
              />
            ))}
          </div>
        </>
      )}

      {confirmDelete ? (
        <ConfirmDialog
          title={`Удалить лимит на «${confirmDelete.category_name}»?`}
          description="Расходы по категории останутся, исчезнет только сам лимит."
          confirmLabel="Удалить"
          destructive
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}

      {creating ? <LimitFormSheet onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <LimitFormSheet limit={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

function LimitRow({
  limit,
  onEdit,
  onDelete,
}: {
  limit: LimitWithProgress;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const usedPct = Math.min(100, limit.used_pct);
  const projected = Math.min(150, limit.projected_pct);
  const tone =
    limit.status === 'over' ? 'over' : limit.status === 'at_risk' ? 'risk' : 'ok';
  return (
    <div className={`limit-row limit-${tone}`}>
      <div className="limit-head">
        <CategoryMark name={limit.category_name} slug={limit.category_slug} size="sm" />
        <div className="limit-main">
          <div className="limit-name">{limit.category_name}</div>
          <div className="limit-amounts">
            <b>{formatWithCurrency(limit.spent_minor, CURRENCY)}</b>
            <span className="muted">
              {' '}/ {formatWithCurrency(limit.monthly_amount_minor, CURRENCY)}
            </span>
          </div>
        </div>
        <div className="limit-actions">
          <button type="button" className="icon-btn" onClick={onEdit} aria-label="Изменить">
            <PencilIcon size={18} />
          </button>
          <button type="button" className="icon-btn" onClick={onDelete} aria-label="Удалить">
            <TrashIcon size={18} />
          </button>
        </div>
      </div>
      <div className="limit-bar">
        <div className="limit-bar-fill" style={{ width: `${usedPct}%` }} />
      </div>
      <div className="limit-meta">
        <span>{usedPct.toFixed(0)}% использовано</span>
        {limit.projected_pct > 0 ? (
          <span className={projected > 100 ? 'warn' : ''}>
            прогноз: {projected.toFixed(0)}% к концу месяца
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ----- Form sheet (create/edit) ----------------------------------------------

function LimitFormSheet({
  limit,
  onClose,
}: {
  limit?: LimitWithProgress;
  onClose: () => void;
}): JSX.Element {
  const isEdit = !!limit;
  const [categoryId, setCategoryId] = useState(limit?.category_id ?? '');
  const [amountMajor, setAmountMajor] = useState(
    limit ? (BigInt(limit.monthly_amount_minor) / 100n).toString() : '',
  );
  const [note, setNote] = useState(limit?.note ?? '');

  const queryClient = useQueryClient();
  const toast = useToast();
  const categoriesQ = useQuery({
    queryKey: ['categories', 'expense'],
    queryFn: () => api.categories('expense', false),
  });

  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const upsertMut = useMutation({
    mutationFn: (input: { category_id: string; monthly_amount_minor: string; note?: string | null }) =>
      api.upsertLimit(input),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['limits'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить лимит')),
  });

  const minor = parseAmountToMinor(amountMajor);
  const isValid = !!categoryId && minor !== null && minor > 0n;

  useMainButton({
    text: isEdit ? 'Сохранить' : 'Создать лимит',
    onClick: () => {
      if (!isValid || minor === null) return;
      upsertMut.mutate({
        category_id: categoryId,
        monthly_amount_minor: minor.toString(),
        note: note.trim() || null,
      });
    },
    enabled: isValid && !upsertMut.isPending,
    progress: upsertMut.isPending,
  });

  // Уже существующие лимиты — чтобы не предлагать дубль.
  const existingQ = useQuery({ queryKey: ['limits'], queryFn: () => api.limits() });
  const existingIds = new Set(existingQ.data?.limits.map((l) => l.category_id) ?? []);
  const allCategories = categoriesQ.data ?? [];
  const availableCats: Category[] = isEdit
    ? allCategories
    : allCategories.filter((c) => !existingIds.has(c.id));

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">{isEdit ? 'Изменить лимит' : 'Новый лимит'}</div>

        <label className="form-field">
          <span className="form-label">Категория</span>
          {isEdit ? (
            <input
              type="text"
              className="form-input"
              value={limit?.category_name ?? ''}
              disabled
            />
          ) : (
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="form-select"
              disabled={categoriesQ.isLoading}
            >
              <option value="">Выберите категорию</option>
              {availableCats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="form-field">
          <span className="form-label">Лимит в месяц (UZS)</span>
          <input
            type="text"
            inputMode="decimal"
            className={`form-input ${!isValid && amountMajor ? 'invalid' : ''}`}
            value={amountMajor}
            onChange={(e) => setAmountMajor(e.target.value)}
            placeholder="500 000"
            autoFocus
          />
          <span className="form-hint">
            Годовой расход (страховка, отпуск) разделите на 12 и впишите сюда.
          </span>
        </label>

        <label className="form-field">
          <span className="form-label">Заметка</span>
          <input
            type="text"
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="Необязательно: годовая страховка / 12"
          />
        </label>

        <div className="sheet-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={upsertMut.isPending}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

function parseAmountToMinor(raw: string): bigint | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ''] = cleaned.split('.');
  const padded = (fracPart + '00').slice(0, 2);
  const combined = `${intPart}${padded}`.replace(/^0+/, '') || '0';
  return BigInt(combined);
}
